<script>

	import { AnchorContainer, AnchorContent, AnchorLink } from '@/components/AnchorNav'
	import Layout from '@/components/Products/layout'
	import SectionNav from '@/components/Products/SectionNav'
	import Fragment from '@/components/Fragment'
	import Grid from '@/components/Grid'
	import { Content, Resources } from '@/components/RemoteResources'
	import ShadowedImage from '@/components/ShadowedImage'
	import VideoJs from "@/components/VideoJs"
	import Parallax from '@/components/Parallax'

	export default {
		metaInfo () {
			return this.$generateMetaInfo({
				title: `StudySync Remote Resources`,
				description: `StudySync appreciates the challenges educators are facing to guarantee that students have meaningful learning opportunities apart from classroom-based instruction. Our remote resources page provides comprehensive support for distance learning.`,
				image: 'https://www.studysync.com/static/homepage4.jpg'
			})
		},
		render () {
			return (
				<AnchorContainer
					useDotNav={true}
					scopedSlots={{
						default: (anchorData) => (

							<Layout mastheadBackground="pd/100001/kids3_loop_1">
								<template slot="preHeadline">
									<mark>StudySync</mark> Remote Resources
								</template>
								<template slot="headline">
									Comprehensive Support for Distance Learning
								</template>
								<template slot="nav">
									<SectionNav>
										<template slot="navTitle">Remote Resources</template>
										<template slot="navList">
											<ul>
												{Object.entries(anchorData.anchors).map(([key, { title }]) => (
													<li key={key}>
														<AnchorLink to={key}>{title}</AnchorLink>
													</li>
												))}
											</ul>
										</template>
									</SectionNav>
								</template>
								<template slot="body">

									<AnchorContent el="section" anchor="get-started" title="Get Started">
										<container>
											<div class="section-block">
												<Grid stacked={!this.$mq.desktop} alignCenter={true}>
													<Grid.Column flex={16}>
														<Content>
															<Content.Headline
																pre="Get Started"
																primary="Distance learning. Anywhere. Anytime."
															/>
															<p>StudySync appreciates the challenges you are facing to guarantee that your students have meaningful learning opportunities apart from classroom-based instruction. We know that planning for ongoing learning activities, communicating expectations to students, and following up with them to ensure their progress are amongst your highest priorities. The StudySync team is committed to sharing this responsibility with you.</p>
															<p>Contact us at <a href="mailto:implementation@studysync.com?subject=Back-to-school">implementation@studysync.com</a> for help designing a distance learning plan to meet your needs.</p>
														</Content>
													</Grid.Column>
													{this.$mq.desktop && (
														<Grid.Column flex={9}>
															<ShadowedImage
																src={require('./img/laptop.png')}
																shadow={require('@/assets/img/shadow-left-to-right.svg')}
																position="bottom-center"
																type="img"
															/>
														</Grid.Column>
													)}
												</Grid>
											</div>
											<div class="section-block">
												<Resources>
													<Resources.Item
														title="New to StudySync?"
														description="Contact us anytime for more information, free trials, or a custom webinar designed to meet your district’s needs with the fastest-growing ELA curriculum."
														cta={
															<Fragment>
																<Resources.Item.Cta
																	label="Free Trial"
																	to={{ name: 'freeTrial' }}
																/>
																<Resources.Item.Cta
																	label="Free Webinars"
																	to={{ name: 'webinars' }}
																/>
															</Fragment>
														}
													/>
													<Resources.Item
														title="Distance Learning: The Basics"
														description="Teachers are being asked to transition their offline courses online. If you are new to engaging students online, here's a quick guide to getting started with distance learning."
														cta={
															<Resources.Item.Cta
																label="Read"
																href="http://content.studysync.com/support/Getting-Started-Online-Learning.pdf"
															/>
														}
													/>
													<Resources.Item
														title="Flexible Learning Solutions"
														description="Anytime, Anywhere, Any Device. This year, whether you plan to maintain a traditional or online learning model for your teachers and students, StudySync’s print and digital hybrid design is the solution."
														cta={
															<Resources.Item.Cta
																label="Read"
																href="https://content.studysync.com/support/07/Flexible_Learning_Solution_StudySync_BTS.pdf"
															/>
														}
													/>
												</Resources>
											</div>
										</container>
									</AnchorContent>

									<AnchorContent el="section" anchor="learn" title="Learn">
										<container>
											<div class="section-block">
												<div class="masterclass-wrapper">
													<div class="section-block">
														<Content centered={true}>
															<Content.Headline
																pre="Learn"
																primary="The StudySync Online Learning Series"
															/>
															<p>In this introductory video, Dr. Catlin Tucker provides an overview of the teacher's roles and responsibilities in an online course. The teaching presence in an online environment is critical to the success of an online course.</p>
														</Content>
													</div>
													<div class="section-block">
														<div class="masterclass-video">
															<VideoJs videoServer="pd/100001/catlinol" autoplay={false} class="videojs" />
														</div>
													</div>
												</div>
											</div>
											<div class="section-block">
												<Resources>
													<Resources.Item
														title="Online Instruction"
														description="This episode focuses on Online Instruction. There are two ways teachers can approach instruction online, Asynchronous video recordings and Syncronous video conferencing sessions."
														image="//video-server.studysync.com/gen/pd/100001/mp4/catlinol1.jpg"
														cta={
															<Resources.Item.Cta
																label="Watch"
																video={{
																	src: 'pd/100001/catlinol1',
																	cc: true
																}}
															/>
														}
													/>
													<Resources.Item
														title="Online Instruction using StudySync"
														description="This episode will highlight how teachers can leverage the videos and resources inside of StudySync to simplify online instruction and save time."
														image="//video-server.studysync.com/gen/pd/100001/mp4/catlinol2.jpg"
														cta={
															<Resources.Item.Cta
																label="Watch"
																video={{
																	src: 'pd/100001/catlinol2',
																	cc: true
																}}
															/>
														}
													/>
													<Resources.Item
														title="Facilitating Discussions Online"
														description="This episode focuses on the teacher’s role as facilitator of online discourse."
														image="//video-server.studysync.com/gen/pd/100001/mp4/catlinol3.jpg"
														cta={
															<Resources.Item.Cta
																label="Watch"
																video={{
																	src: 'pd/100001/catlinol3',
																	cc: true
																}}
															/>
														}
													/>
													<Resources.Item
														title="Facilitating Discussions Online using StudySync"
														description="This episode highlights how teachers can simplify the process of preparing to facilitate synchronous online discussions."
														image="//video-server.studysync.com/gen/pd/100001/mp4/catlinol4.jpg"
														cta={
															<Resources.Item.Cta
																label="Watch"
																video={{
																	src: 'pd/100001/catlinol4',
																	cc: true
																}}
															/>
														}
													/>
													<Resources.Item
														title="Designing an Online Course"
														description="This episode focuses on the teacher’s role as the designer of an online course."
														image="//video-server.studysync.com/gen/pd/100001/mp4/catlinol5.jpg"
														cta={
															<Resources.Item.Cta
																label="Watch"
																video={{
																	src: 'pd/100001/catlinol5',
																	cc: true
																}}
															/>
														}
													/>
													<Resources.Item
														title="Designing an Online Course Using StudySync"
														description="This episode highlights how StudySync's units save teacher's time designing and organizing their courses."
														image="//video-server.studysync.com/gen/pd/100001/mp4/catlinol6.jpg"
														cta={
															<Resources.Item.Cta
																label="Watch"
																video={{
																	src: 'pd/100001/catlinol6',
																	cc: true
																}}
															/>
														}
													/>
												</Resources>
											</div>
											<div class="section-block">
												<div class="masterclass-wrapper">
													<div class="section-block">
														<Content centered={true}>
															<p>The following additional resources can be used to supplement this video series:<br />
															<a style="color:#fdb913;" href="https://content.studysync.com/support/ifd/TipsCreatingInstructionalVideos.pdf" target="_blank">Tips for Creating Instructional Videos</a><br />
															<a style="color:#fdb913;" href="https://content.studysync.com/support/ifd/SmallGroupDiscusssion.pdf" target="_blank">Small Group Discussion Planning Document</a><br />
															<a style="color:#fdb913;" href="https://content.studysync.com/support/ifd/FacilitationStrategies.pdf" target="_blank">Facilitation Strategies for an Online Discussion</a><br />
															<a style="color:#fdb913;" href="https://content.studysync.com/support/ifd/3PartFlippedLesson.pdf" target="_blank">Design a Three-Part Flipped Lesson</a>
															<br /><br /><br /></p>
															<p>In this summary video, Dr. Catlin Tucker says that teaching online may feel foreign and unfamiliar. The truth is that teachers are just as important now as they have ever been.</p>
														</Content>
													</div>
													<div class="section-block">
														<div class="masterclass-video">
															<VideoJs videoServer="pd/100001/catlinol7" autoplay={false} class="videojs" />
														</div>
													</div>
												</div>
											</div>
										</container>
									</AnchorContent>

									<AnchorContent el="section" anchor="design" title="Design" class="light">
										<container>
											<Content centered={true}>
												<Content.Headline
													pre="Design"
													primary="Design Online Learning"
												/>
													<Resources>
														<Resources.Item
															title="5-DAY MINI UNITS"
															description="Explore our 5-Day Mini Units that provide guidance on using StudySync to support continuous learning outside of the classroom. And, watch our Facebook Live presentation about the 5-Day Mini Units."
															cta={
																<Fragment>
																	<Resources.Item.Cta
																		label="View"
																		to="/blog/remote-instruction-resource-5-day-mini-units"
																	/>
																	<Resources.Item.Cta
																		label="Watch"
																		href="https://www.facebook.com/watch/live/?v=212039560104873"
																	/>
																</Fragment>
															}
														/>
														<Resources.Item
															title="10-Day Standards-Based Units"
															description="Built from resources found at each grade level, these abbreviated units feature a “Big Idea” Blast, a Close Reading selection, and a “Current Connections” write assignment. To find these units, open your Library tab, select Units in the gray menu bar, then type “mini unit” in the search bar."
														/>
														<Resources.Item
															title="UNIT CREATOR"
															description="Intuitive and Easy. Build your own customized units from StudySync’s extensive Library of resources."
															cta={
																<Fragment>
																	<Resources.Item.Cta
																		label="Read Overview"
																		href="http://content.studysync.com/support/2/UnitCreator_flyer.pdf"
																	/>
																	<Resources.Item.Cta
																		label="Comprehensive Guide"
																		href="http://content.studysync.com/support/3/Unit_Creator-MK-V7-accessible.pdf"
																	/>
																</Fragment>
															}
														/>
														<Resources.Item
															title="Guest Teacher Guide"
															description="Whether substituting for just one day or longer, this guide offers resources and ready-to-go ideas that can be utilized with or without login access to StudySync's digital platform."
															cta={
																<Resources.Item.Cta
																	label="Read"
																	href="https://www.studysync.com/blog/solutions-for-guest-teachers"
																/>
															}
														/>

														<Resources.Item
															title="Tips For Hybrid and Remote Lesson Planning"
															description="Resources and tips to help teachers plan their StudySync courses, lessons, and even parent communication no matter what your learning environment looks like this year."
															cta={
																<Resources.Item.Cta
																	label="Read"
																	href="https://www.studysync.com/blog/hybrid-remote-lesson-planning"
																/>
															}
														/>
														<Resources.Item
															title="Less is More"
															description="Less is More with Distance Learning: Customize Your StudySync Lessons, part of Dr. Catlin Tucker’s Remote Learning Blog Series."
															cta={
																<Resources.Item.Cta
																	label="View"
																	to="/blog/less-is-more-with-distance-learning-customize-your-studysync-lessons"
																/>
															}
														/>
													<Resources.Item
														title="Prior Knowledge"
														description="Access Prior Knowledge Activities to Facilitate Online Discussion, part of Dr. Catlin Tucker’s Remote Learning Blog Series."
														cta={
															<Resources.Item.Cta
																label="View"
																to="/blog/use-the-studysync-access-prior-knowledge-activities-to-facilitate-online-discussions"
															/>
														}
													/>
													<Resources.Item
														title="Engage Students"
														description="Engage Students in a Collaborative Research Activity, part of Dr. Catlin Tucker’s Remote Learning Blog Series."
														cta={
															<Resources.Item.Cta
																label="View"
																to="blog/create-collaborative-online-research-activities-with-building-background-activities"
															/>
														}
													/>
													<Resources.Item
														title="StudySync's Full Potential"
														description="Teacher Training and PD is available showing how to use StudySync distance learning resources to their full potential. Basic to advanced training is available."
														cta={
															<Resources.Item.Cta
																label="Contact Us"
																to={{ name: 'contact' }}
															/>
														}
													/>
												</Resources>
											</Content>
										</container>
									</AnchorContent>

									<Parallax src={require('@/assets/img/resource1.jpg')} class="parallax-banner" />

									<AnchorContent el="section" anchor="instruct" title="Instruct" class="light">
										<container>
											<Content centered={true}>
												<Content.Headline
													pre="Instruct"
													primary="Instruct from anywhere"
												/>
												<Resources>
													<Resources.Item
														title="Tips for Synchronous Online Discussion"
														description="A 3-part video series from Dr. Catlin Tucker, including downloadable templates, to guide teachers through the process of preparing, facilitating, and wrapping up online conversations."
														cta={
															<Resources.Item.Cta
																label="Read"
																href="https://www.studysync.com/blog/prepare-facilitate-wrap-it-up"
															/>
														}
													/>
													<Resources.Item
														title="Support for Small Groups"
														description="Identify Students Who Need More Support & Offer Virtual Small Group Sessions."
														cta={
															<Resources.Item.Cta
																label="View"
																to="/blog/identify-students-who-need-more-support-offer-virtual-small-group-sessions"
															/>
														}
													/>
													<Resources.Item
														title="Record a Screencast"
														description="Model the reading comprehension strategy for students. As students learn remotely, they will benefit from accessing video content that helps them to navigate tasks online and engage with digital texts."
														cta={
															<Resources.Item.Cta
																label="View"
																to="/blog/record-a-screencast-modeling-the-reading-comprehension-strategy-for-students"
															/>
														}
													/>
													<Resources.Item
														title="Google Integration for Teachers"
														description="StudySync’s recently enhanced Google integration provides opportunities for teachers to utilize both Google Drive and Google Classroom in conjunction with StudySync to leverage both platforms to enhance teaching and learning."
														cta={
															<Resources.Item.Cta
																label="Read"
																href="http://content.studysync.com/support/2/191108_all_products_Google_Integration_Classroom.pdf"
															/>
														}
													/>
													<Resources.Item
														title="Student Mobile App"
														description="StudySync provides a mobile companion app to the StudySync ELA Curriculum that, once assignments havebeen downloaded, allows students to work on and complete assignments using a phone or tablet."
														cta={
															<Resources.Item.Cta
																label="Read"
																href="http://content.studysync.com/support/2/190903_NN_MobileApp_document_FNLpdf.pdf"
															/>
														}
													/>
													<Resources.Item
														title="StudySync from Home"
														description="A simple guide for parents on how students can use StudySync from any internet enabled device to access assigned classwork and browse our dynamic library."
														cta={
															<Resources.Item.Cta
																label="Read"
																href="https://content.studysync.com/support/07/Family_Overview.pdf"
															/>
														}
													/>
												</Resources>
											</Content>
										</container>
									</AnchorContent>

									<Parallax src={require('@/assets/img/resource2.jpg')} class="parallax-banner" />

									<AnchorContent el="section" anchor="facilitate" title="Facilitate" class="light">
										<container>
											<Content centered={true}>
												<Content.Headline
													pre="Facilitate"
													primary="Facilitate Digital Education"
												/>
												<Resources>
													<Resources.Item
														title="Synchronous Discussions"
														description="Dr. Catlin Tucker presents seven suggestions to help teachers maximize student engagement in online, synchronous discussions."
														cta={
															<Resources.Item.Cta
																label="View"
																to="/blog/7-strategies-for-engaging-students-in-synchronous-online-discussions"
															/>
														}
													/>
													<Resources.Item
														title="Supporting Students with IEPs"
														description="StudySync discusses best practices for supporting students on IEPs via remote learning."
														cta={
															<Resources.Item.Cta
																label="Watch"
																href="https://www.facebook.com/watch/live/?v=818692871989482"
															/>
														}
													/>
													<Resources.Item
														title="Vocabulary Deep Dive"
														description="Facilitate a deep dive into vocabulary with Google Slides & the Frayer Model. Explore vocabulary with students."
														cta={
															<Resources.Item.Cta
																label="View"
																to="/blog/facilitate-a-deep-dive-into-vocabulary-with-google-slides-the-frayer-model"
															/>
														}
													/>
													<Resources.Item
														title="A Teacher's Perspective"
														description="English teacher, Katharine Hertz, reflects on how to proceed in these unprecedented times. Kate is a National Board Certified Teacher and English Department Instructional Coach at Geneva High School in Geneva, IL."
														cta={
															<Resources.Item.Cta
																label="View"
																to="/blog/a-teachers-perspective-on-change"
															/>
														}
													/>
													<Resources.Item
														title="Parent and Guardian Outreach"
														description="Learn 5 strategies, from Alyssa Owens, to build strong collaboration between home and school to help families manage at-home learning and foster student success."
														cta={
															<Resources.Item.Cta
																label="View"
																to="/blog/five-strategies-for-parent-and-guardian-outreach"
															/>
														}
													/>
													<Resources.Item
														title="Support Remote Learning"
														description="Joan Romano delivers a message of support to administrator, from the perspective of a former site and district administrator in Los Angeles."
														cta={
															<Resources.Item.Cta
																label="View"
																to="/blog/supporting-remote-learning"
															/>
														}
													/>
												</Resources>
											</Content>
										</container>
									</AnchorContent>

								</template>
								<template slot="footer">
									<div class="footer" />
								</template>
							</Layout>
						)
					}}
				/>
			)
		}
	}

</script>

<style lang="scss" scoped>

	@import "media.scss";

	.section-block {
		& + .section-block {
			margin-top:3rem;
			@include media(xlarge) {
				margin-top:5rem;
			}
		}
	}

	.masterclass {
		&-wrapper {
			max-width:1000px; margin:0 auto;
		}
		&-video {
			position:relative; padding-bottom:56.25%;
			.videojs {
				position:absolute; top:0; left:0;
			}
		}
	}

	.parallax-banner {
		height:25vh; background:transparent;
		@include media(xlarge) {
			height:50vh;
		}
	}

	.footer {
		height:30vh; background:url('./img/footer.jpg') top center no-repeat; background-size:cover;
		@include media(xlarge) {
			height:60vh;
		}
	}

</style>



// WEBPACK FOOTER //
// src/routes/remote-resources/index.vue